<template>
  <b-card :title="isEdit ? 'Editar Integração' : 'Nova Integração Bancária'">
    <b-form @submit.prevent="save">
      <b-row>
        <b-col md="6">
          <b-form-group label="Descrição da Configuração *">
            <b-form-input
              v-model="form.descricao"
              required
              placeholder="Ex: Conta PagBank Principal, Cora Homologação..."
            />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Provedor (Banco) *">
            <v-select
              v-model="form.provedor"
              :options="provedores"
              required
              placeholder="Selecione o banco"
            />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Ambiente *">
            <v-select
              v-model="form.ambiente"
              :options="ambientes"
              required
              placeholder="Sandbox ou Produção"
            />
          </b-form-group>
        </b-col>

        <!-- Credentials -->
        <b-col md="6">
          <b-form-group label="Client ID / Application Key">
            <b-form-input
              v-model="form.clientId"
              placeholder="ID da aplicação fornecido pelo banco"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Client Secret / Token Account">
            <b-input-group>
              <b-form-input
                v-model="form.clientSecret"
                :type="showSecret ? 'text' : 'password'"
                placeholder="Segredo ou Token da aplicação"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showSecret ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showSecret = !showSecret"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-group label="Webhook URL (Para notificações em tempo real)">
            <b-form-input
              v-model="form.webhookUrl"
              placeholder="Ex: https://sua-url-ngrok.dev/financeiro/webhooks/pagbank"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Webhook Secret (Opcional)">
            <b-form-input
              v-model="form.webhookSecret"
              placeholder="Chave para validar notificações de pagamento"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Token de Acesso Direto (Sandbox/Produção)">
            <b-form-input
              v-model="form.tokenAtual"
              placeholder="Cole aqui o token gerado no portal do desenvolvedor"
            />
          </b-form-group>
        </b-col>

        <!-- Certificate mTLS (Santander specific) -->
        <b-col v-if="form.provedor === 'SANTANDER'" md="12">
          <b-alert show variant="info" class="mb-1">
            <div class="alert-body">
              <feather-icon icon="InfoIcon" class="mr-50" />
              <span>O Santander exige um certificado **.p12 (A1)** para autenticação mTLS.</span>
            </div>
          </b-alert>
          <b-form-group label="Certificado A1 (.p12)">
            <b-form-file
              placeholder="Selecione o arquivo .p12..."
              drop-placeholder="Solte o arquivo aqui..."
              accept=".p12"
              @change="handleFileUpload"
            />
            <small v-if="form.certificadoA1" class="text-success mt-1 d-block">
              <feather-icon icon="CheckIcon" /> Certificado carregado e pronto para envio.
            </small>
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-checkbox v-model="form.ativo" switch class="mt-1">
            Integração Ativa
          </b-form-checkbox>
        </b-col>
      </b-row>

      <hr class="my-2" />

      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" @click="cancel">
          Cancelar
        </b-button>
        <b-button type="submit" variant="primary">
          <feather-icon icon="SaveIcon" class="mr-50" />
          {{ isEdit ? 'Salvar Alterações' : 'Cadastrar Integração' }}
        </b-button>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BFormGroup, BFormInput, BFormCheckbox, BButton, BInputGroup, BInputGroupAppend, BAlert, BFormFile
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapState } from 'vuex'
import mixinsGlobal from '@/mixins'

export default {
  name: 'IntegracaoGatewayForm',
  components: {
    BCard, BRow, BCol, BForm, BFormGroup, BFormInput, BFormCheckbox, BButton, BInputGroup, BInputGroupAppend, BAlert, BFormFile,
    vSelect
  },
  mixins: [mixinsGlobal],
  data() {
    return {
      isEdit: false,
      showSecret: false,
      form: {
        id: null,
        descricao: '',
        provedor: 'PAGBANK',
        ambiente: 'SANDBOX',
        clientId: '',
        clientSecret: '',
        webhookSecret: '',
        webhookUrl: '',
        tokenAtual: '',
        ativo: true,
        certificadoA1: null
      },
      provedores: ['PAGBANK', 'CORA', 'SANTANDER', 'ASAAS', 'INTER', 'BB', 'CAIXA'],
      ambientes: ['SANDBOX', 'PRODUCAO']
    }
  },
  computed: {
    ...mapState('bankIntegrationModule', ['gateway'])
  },
  watch: {
    gateway: {
      handler(val) {
        if (val && val.id) {
          this.form = { ...val }
          this.isEdit = true
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.$route.params.id) {
      this.isEdit = true
      this.loadGateway()
    } else {
      this.$store.dispatch('bankIntegrationModule/reset')
    }
  },
  methods: {
    async loadGateway() {
      try {
        await this.$store.dispatch('bankIntegrationModule/loadById', this.$route.params.id)
      } catch (e) {
        this.MensagemError('Erro', 'Falha ao carregar configuração.')
        this.cancel()
      }
    },
    handleFileUpload(e) {
      const file = e.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (event) => {
        // Obter apenas a string Base64 do DataURL
        const base64 = event.target.result.split(',')[1]
        this.form.certificadoA1 = base64
      }
      reader.readAsDataURL(file)
    },
    async save() {
      if (!this.form.descricao || !this.form.provedor) {
        this.MensagemError('Validação', 'Descrição e Provedor são campos obrigatórios.')
        return
      }

      try {
        let response
        if (this.isEdit) {
          response = await this.$store.dispatch('bankIntegrationModule/update', {
            id: this.form.id,
            data: this.form
          })
        } else {
          response = await this.$store.dispatch('bankIntegrationModule/insert', this.form)
        }

        if (response && response.status === 201 || response.status === 200) {
          this.showMessageSuccess('Sucesso', `Integração ${this.isEdit ? 'atualizada' : 'cadastrada'} com sucesso!`)
          this.cancel()
        }
      } catch (e) {
        this.MensagemError('Erro', 'Ocorreu um erro ao salvar a configuração.')
      }
    },
    cancel() {
      this.$router.push({ name: 'integracoes' })
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
